import React, { useState, useRef } from "react";
import "./Contracting.css"; // Custom CSS for styling the form
import logoSrc from "../img/logo.png";
import ContactForm from "../ContactForm"; // Make sure this component has the `openForm` method.

const JobApplicationForm = () => {
  const contactFormRef = useRef(); // Reference for controlling the contact form

  // Function to open the contact form
  const handleOpenContactForm = () => {
    if (contactFormRef.current) {
      contactFormRef.current.openForm(); // Ensure the `ContactForm` has this method.
    }
  };

  const [formData, setFormData] = useState({
    applicantName: "",
    contactInfo: "",
    positionAppliedFor: "",
    educationLevel: "",
    workExperience: "",
    skillsCertifications: "",
    availability: "",
    expectedSalary: "",
    resume: null,
  });

  const [snackbar, setSnackbar] = useState({
    message: "",
    show: false,
    type: "success",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, resume: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form data:", formData);

    // Create a form data object for file submission
    const data = new FormData();
    for (const [key, value] of Object.entries(formData)) {
      data.append(key, value);
    }

    try {
      const response = await fetch(
        "https://apiqms.shigjeta.com/api/forms/job-application/submit",
        {
          method: "POST",
          body: data,
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        console.log("Server response:", responseData);
        // Show success snackbar
        setSnackbar({
          message: "Application submitted successfully!",
          show: true,
          type: "success",
        });
      } else {
        const errorData = await response.json();
        console.log("Error response:", errorData);
        // Show error snackbar
        setSnackbar({
          message: errorData.message || "An error occurred. Please try again.",
          show: true,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      // Show error snackbar
      setSnackbar({
        message: "An error occurred. Please try again.",
        show: true,
        type: "error",
      });
    }
  };

  // Snackbar styling
  const snackbarStyle = {
    position: "fixed",
    top: "20px", // Move it to the top
    left: "50%", // Center horizontally
    transform: "translateX(-50%)", // Adjust to perfectly center
    backgroundColor: snackbar.type === "success" ? "#16a085" : "#e74c3c", // Green for success, red for error
    color: "white",
    padding: "15px 30px",
    borderRadius: "8px",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
    zIndex: 1001,
    animation: "fadeIn 0.5s ease-out",
  };

  const dismissButtonStyle = {
    background: "none",
    border: "none",
    color: "white",
    fontSize: "16px",
    cursor: "none",
    fontWeight: "bold",
    marginLeft: "15px", // Adds a gap between the text and the button
  };

  // Close snackbar after 5 seconds
  setTimeout(() => {
    if (snackbar.show) {
      setSnackbar({ ...snackbar, show: false });
    }
  }, 5000);

  return (
    <>
      <div className="banner-logo" id="sectionBanner1">
        {logoSrc && <img src={logoSrc} alt="Job Application Logo" />}
      </div>

      <section className="banner-section" id="sectionBanner">
        <div className="banner-container">
          <div className="banner-text">
            <h1 className="banner-title" id="titulli">
              start your journey with us, apply today
            </h1>
            <p className="banner-description">
              Apply for your desired position with us.
            </p>
            <a
              onClick={handleOpenContactForm} // Open the form on button click
              className="banner-button"
            >
              Start Now
            </a>
          </div>
          <div className="shop-hero-background"></div>
        </div>
      </section>

      <div className="img-background">
        <img
          className="img"
          id="banner1-video"
          src="/img/Contracting Thumbnail.png" // This should be correct if the video is in the public folder
          autoPlay
          muted
          loop
          playsInline
          onError={(e) => console.error("Error loading video:", e)} // Error logging for debugging
        />
      </div>

      {/* Form Section */}
      <h3 className="textForm">Shigjeta LLC - Job application form</h3>
      <section className="contract-form-container">
        <form onSubmit={handleSubmit} className="contract-form">
          <div className="divFlexField">
            <div className="form-group">
              <label>Applicant Name</label>
              <input
                type="text"
                id="applicantName"
                name="applicantName"
                value={formData.applicantName}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
            </div>
            <div className="form-group">
              <label>Contact Information</label>
              <input
                type="text"
                id="contactInfo"
                name="contactInfo"
                value={formData.contactInfo}
                onChange={handleChange}
                placeholder="Enter your phone or email"
                required
              />
            </div>
          </div>

          <div className="divFlexField">
            <div className="form-group">
              <label>Position Applied For</label>
              <input
                type="text"
                id="positionAppliedFor"
                name="positionAppliedFor"
                value={formData.positionAppliedFor}
                onChange={handleChange}
                placeholder="Enter the position you're applying for"
                required
              />
            </div>
            <div className="form-group">
              <label>Education Level</label>
              <input
                type="text"
                id="educationLevel"
                name="educationLevel"
                value={formData.educationLevel}
                onChange={handleChange}
                placeholder="Enter your highest level of education"
              />
            </div>
          </div>

          <div className="divFlexField">
            <div className="form-group">
              <label>Work Experience</label>
              <textarea
                id="workExperience"
                name="workExperience"
                value={formData.workExperience}
                onChange={handleChange}
                placeholder="Describe your work experience"
              />
            </div>
            <div className="form-group">
              <label>Skills/Certifications</label>
              <textarea
                id="skillsCertifications"
                name="skillsCertifications"
                value={formData.skillsCertifications}
                onChange={handleChange}
                placeholder="List your skills or certifications"
              />
            </div>
          </div>

          <div className="divFlexField">
            <div className="form-group">
              <label>Availability</label>
              <select
                id="availability"
                name="availability"
                value={formData.availability}
                onChange={handleChange}
              >
                <option value="full-time">Full-Time</option>
                <option value="part-time">Part-Time</option>
              </select>
            </div>
            <div className="form-group">
              <label>Expected Salary</label>
              <input
                type="text"
                id="expectedSalary"
                name="expectedSalary"
                value={formData.expectedSalary}
                onChange={handleChange}
                placeholder="Enter your expected salary"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Attach Resume/CV</label>
            <input
              type="file"
              id="resume"
              name="resume"
              onChange={handleFileChange}
              required
            />
          </div>

          <button type="submit" className="submit-button">
            Submit Application
          </button>
        </form>
      </section>

      {/* Snackbar display */}
      {snackbar.show && (
        <div style={snackbarStyle}>
          <span>{snackbar.message}</span>
          <button
            onClick={() => setSnackbar({ ...snackbar, show: false })}
            style={dismissButtonStyle}
          >
            X
          </button>
        </div>
      )}

      <ContactForm ref={contactFormRef} />
    </>
  );
};

export default JobApplicationForm;
